import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Calendar, User, Crown, Users, ShoppingBag } from 'lucide-react';

/**
 * Public mobile bottom navigation.
 * 5 slots with the central FULL BRAVX rendered as a "protruding" floating action
 * button (FAB-style). The right-most slot opens the Comunidad drawer via a
 * window event consumed by `GlobalCommunityDrawer`.
 */
const MobileBottomNav = () => {
  const location = useLocation();

  if (
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/producer') ||
    location.pathname.startsWith('/partner') ||
    location.pathname.startsWith('/prensa') ||
    location.pathname.startsWith('/productora/login') ||
    location.pathname.startsWith('/productora/aplicar') ||
    location.pathname.startsWith('/validador') ||
    location.pathname.startsWith('/validator')
  ) {
    return null;
  }

  const sideItems = [
    { path: '/', icon: Home, label: 'Inicio' },
    { path: '/eventos', icon: Calendar, label: 'Eventos' },
    { path: '/tienda', icon: ShoppingBag, label: 'Merch' },
    // BRAVX goes in the middle — handled separately for the destacado styling.
    { path: '/mi-cuenta', icon: User, label: 'Cuenta' },
    { type: 'community', icon: Users, label: 'Comunidad' },
  ];

  const isActive = (path) => location.pathname === path ||
    (path !== '/' && location.pathname.startsWith(path));

  const bravxActive = location.pathname === '/fullbravx' || location.pathname.startsWith('/fullbravx');

  const renderSideItem = (item, idx) => {
    const Icon = item.icon;
    const active = item.type !== 'menu' && isActive(item.path);

    if (item.type === 'community') {
      return (
        <button
          key="community"
          onClick={() => { try { window.dispatchEvent(new CustomEvent('open-community-drawer')); } catch (_) {} }}
          className="flex flex-col items-center justify-center flex-1 py-2 group"
          data-testid="mobile-nav-community-trigger"
        >
          <motion.div
            whileTap={{ scale: 0.9 }}
            className="relative flex flex-col items-center gap-1 text-[#71717A] group-hover:text-[#D4AF37]"
          >
            <Icon size={22} strokeWidth={2} />
            <span className="text-[10px] font-medium uppercase tracking-wider">
              {item.label}
            </span>
          </motion.div>
        </button>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        className="flex flex-col items-center justify-center flex-1 py-2 group"
        data-testid={`mobile-nav-${item.label.toLowerCase()}`}
      >
        <motion.div
          whileTap={{ scale: 0.9 }}
          className={`relative flex flex-col items-center gap-1 transition-colors duration-200 ${
            active ? 'text-[#D4AF37]' : 'text-[#71717A]'
          }`}
        >
          {active && (
            <motion.div
              layoutId="bottomNavIndicator"
              className="absolute -top-1 w-8 h-1 bg-[#D4AF37] rounded-full"
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          )}
          <Icon
            size={22}
            strokeWidth={active ? 2.5 : 2}
            className={`transition-transform duration-200 ${active ? 'scale-110' : 'group-active:scale-90'}`}
          />
          <span className={`text-[10px] font-medium uppercase tracking-wider ${
            active ? 'text-[#D4AF37]' : 'text-[#71717A]'
          }`}>
            {item.label}
          </span>
        </motion.div>
      </Link>
    );
  };

  return (
    <motion.nav
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="mobile-bottom-nav"
    >
      {/* Solid black underlay so scroll content never bleeds through */}
      <div className="absolute inset-0 bg-[#0A0A0A]" />
      {/* Glass layer on top */}
      <div className="absolute inset-0 backdrop-blur-xl bg-black/40 border-t border-white/10" />

      {/* Container with notch to make space for the floating BRAVX button */}
      <div className="relative h-16 flex items-end">
        {/* Left side - 3 items */}
        <div className="flex flex-1 items-center justify-around">
          {renderSideItem(sideItems[0])}
          {renderSideItem(sideItems[1])}
          {renderSideItem(sideItems[2])}
        </div>

        {/* Spacer where the FAB sits */}
        <div className="w-16 flex-shrink-0" aria-hidden />

        {/* Right side - 2 items */}
        <div className="flex flex-1 items-center justify-around">
          {renderSideItem(sideItems[3])}
          {renderSideItem(sideItems[4])}
        </div>

        {/* Centered floating FULL BRAVX button (protruding above the bar) */}
        <Link
          to="/fullbravx"
          className="absolute left-1/2 -translate-x-1/2 -top-7 flex flex-col items-center"
          data-testid="mobile-nav-bravx"
        >
          <motion.div
            whileTap={{ scale: 0.92 }}
            whileHover={{ scale: 1.04 }}
            className="relative"
          >
            {/* Outer glow ring */}
            <div className={`absolute inset-0 rounded-full blur-md ${
              bravxActive ? 'bg-yellow-500/60' : 'bg-yellow-500/35'
            }`} />

            {/* Animated gradient ring */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              className="absolute -inset-1 rounded-full opacity-60"
              style={{
                background: 'conic-gradient(from 0deg, #D4AF37, #FCD34D, #F59E0B, #D4AF37)',
              }}
            />

            {/* Main button */}
            <div
              className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-xl"
              style={{
                background: bravxActive
                  ? 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 50%, #D4AF37 100%)'
                  : 'linear-gradient(135deg, #D4AF37 0%, #B8862E 100%)',
                boxShadow: '0 8px 24px rgba(212,175,55,0.5), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              <Crown
                size={26}
                strokeWidth={2.5}
                className="text-black drop-shadow-sm"
              />
            </div>
          </motion.div>

          {/* Label below the FAB */}
          <span
            className={`mt-1 text-[10px] font-black uppercase tracking-[0.15em] ${
              bravxActive ? 'text-yellow-400' : 'text-yellow-500/90'
            }`}
            style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}
          >
            BRAVX
          </span>
        </Link>
      </div>
    </motion.nav>
  );
};

export default MobileBottomNav;
